<template>
  <!-- Page Header Start-->
  <div class="page-main-header" :class="{ open: !togglesidebar }">
    <div class="main-header-right row">
      <!-- Left Section - Menu Icon and Title -->
      <div class="header-left-section">
        <!-- Menu Toggle Icon -->
        <div class="menu-toggle-icon" @click="toggle_sidebar">
          <!-- Sidebar Close Icon (when sidebar is open) -->
          <img v-if="!togglesidebar" src="@/assets/icons/sidebar-close.svg" alt="Close Sidebar" />
          
          <!-- Sidebar Open Icon (when sidebar is closed) -->
          <img v-else src="@/assets/icons/sidebar-open.svg" alt="Open Sidebar" />
        </div>
        
        <!-- Page Title (No Breadcrumbs) -->
        <h1 class="page-title">{{ $route.meta.title }}</h1>
      </div>

      <!-- Right Section - Billing and Profile -->
      <div class="header-right-section">
        <!-- Billing Container -->
        <div class="billing-upgrade-container" v-if="user.role == 'user'">
          <!-- Next Billing Date -->
          <div class="billing-date-section">
            <span class="billing-label">Next Billing Date</span>
            <span class="billing-date" v-if="plan_details == 'PLAN_NOT_FOUND'">-- --- ----</span>
            <span class="billing-date" v-else-if="plan_details.plan_type != 1">{{ formatBillingDate(plan_details.expiry_date) }}</span>
          </div>

          <!-- Upgrade Button -->
          <button class="upgrade-section" @click="upgrade_modal = true">
            <svg width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 17L1.6349 7.85C1.25436 7.434 1.06351 7.226 1.01323 6.976C0.962949 6.726 1.05894 6.473 1.25093 5.968L2.10115 3.735C2.60397 2.415 2.85538 1.755 3.45419 1.378C4.05301 1.001 4.84838 1 6.43798 1H13.562C15.1516 1 15.947 1 16.5447 1.378C17.1446 1.755 17.396 2.415 17.8988 3.735L18.7491 5.968C18.9411 6.473 19.0371 6.725 18.9868 6.976C18.9353 7.226 18.7456 7.434 18.3651 7.85L10 17ZM10 17L13.9997 6M10 17L6.00029 6M13.9997 6L18.5708 7M13.9997 6L12.2855 2M13.9997 6H6.00029M6.00029 6L7.71445 2M6.00029 6L1.4292 7" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <span>Upgrade</span>
          </button>
        </div>

        <!-- Profile Dropdown -->
        <div class="profile-container">
          <div class="profile-dropdown-wrapper">
            <div class="profile-button" @click.stop="toggleProfileDropdown">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="6" r="4" fill="#000000"/>
                <path d="M20 17.5C20 19.9853 20 22 12 22C4 22 4 19.9853 4 17.5C4 15.0147 7.58172 13 12 13C16.4183 13 20 15.0147 20 17.5Z" fill="#000000"/>
              </svg>
              <span>Profile</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="dropdown-arrow" :class="{ 'rotate': showProfileDropdown }">
                <path d="M7 10L12 15L17 10" stroke="#000000" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            
            <!-- Custom Profile Dropdown -->
            <transition name="dropdown-fade">
              <div v-if="showProfileDropdown" class="profile-dropdown-menu" v-click-outside="closeProfileDropdown" @click.stop>
                <!-- Profile Header -->
                <div class="dropdown-header">
                  <div class="profile-icon">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="6" r="4" fill="#000000"/>
                      <path d="M20 17.5C20 19.9853 20 22 12 22C4 22 4 19.9853 4 17.5C4 15.0147 7.58172 13 12 13C16.4183 13 20 15.0147 20 17.5Z" fill="#000000"/>
                    </svg>
                  </div>
                  <span class="profile-title">Profile</span>
                  <router-link v-if="user.role == 'user'" class="view-profile-btn" :to="{ path: '/app/profile' }" @click.native="closeProfileDropdown">
                    View Profile
                  </router-link>
                  <button class="close-btn" @click="closeProfileDropdown">
                    <img src="../assets/icons/profile-close.svg" alt="Close" />
                  </button>
                </div>
                
                <!-- Profile Info Card -->
                <div class="profile-info-card">
                  <div class="info-row">
                    <span class="email">{{ user.email }}</span>
                  </div>
                  <div class="info-row" v-if="user.role == 'user'">
                    <div class="info-item">
                      <span class="label">Next Billing Date :</span>
                      <span class="value" v-if="plan_details == 'PLAN_NOT_FOUND'">--/--/----</span>
                      <span class="value" v-else-if="plan_details.plan_type != 1">{{ formatBillingDate(plan_details.expiry_date) }}</span>
                    </div>
                  </div>
                  <div class="info-row" v-if="user.role == 'user'">
                    <div class="info-item">
                      <span class="label">Your Current Plan</span>
                      <span class="value plan-name" v-if="plan_details != 'PLAN_NOT_FOUND'">{{ plan_details.plan_name }}</span>
                      <span class="value" v-else>No Plan</span>
                    </div>
                  </div>
                </div>
                
                <!-- Logout Button -->
                <button class="logout-btn" @click="logout()">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="#F20707" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <polyline points="16 17 21 12 16 7" stroke="#F20707" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="#F20707" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Log Out</span>
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>

    </div>

    <!-- Upgrade Modal -->
    <b-modal id="modal-upgrade-plan" size="xl" class="theme-modal custom-modal-no-header" @hidden="(upgrade_modal = false)"
      v-model="upgrade_modal" no-close-on-backdrop hide-footer :title="null" hide-header hide-header-close>
      <!-- Custom header inside modal body -->
      <div class="custom-upgrade-modal-header" :class="{ 'no-border': planFound === true && !planExpired }" style="margin: -15px 0 0 -45px;">
        <!-- When no active plan: Show logout button on left, no close button -->
        <button v-if="planFound === false || planExpired" class="logout-modal-btn" @click="logout()">
          <feather type="log-out"></feather>
          <span>Go back to Login</span>
        </button>
        
        <!-- When plan exists: Only show close button on right -->
        <button v-if="planFound === true && !planExpired" class="custom-modal-close" @click="upgrade_modal = false" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
        
        <!-- Default state while loading -->
        <div v-if="planFound === null" style="height: 40px;"></div>
      </div>
      <upgrade-component @plan-status="updatePlanStatus"></upgrade-component>
    </b-modal>
  </div>
  <!-- Page Header Ends -->
</template>
<script>
var body = document.getElementsByTagName("body")[0];
import { mapState } from "vuex";
import UserAuth from '../auth/js/index'
import UpgradeComponent from '../views/UpgradePlan.vue'
import UserController from "../../services/userController";
import DashboardController from "../../services/dashboardController";
import moment from 'moment';
export default {
  name: 'Search',
  components: { UpgradeComponent },
  directives: {
    'click-outside': {
      bind: function(el, binding, vnode) {
        el.clickOutsideEvent = function(event) {
          // Check if the clicked element is outside the dropdown
          if (!(el == event.target || el.contains(event.target))) {
            // Also check if it's not the profile button
            const profileButton = document.querySelector('.profile-button');
            if (!profileButton || !profileButton.contains(event.target)) {
              vnode.context[binding.expression](event);
            }
          }
        };
        setTimeout(() => {
          document.body.addEventListener('click', el.clickOutsideEvent);
        }, 100);
      },
      unbind: function(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  },
  data() {
    return {
      upgrade_modal: false,
      terms: '',
      searchOpen: false,
      searchResult: false,
      searchResultEmpty: false,
      toggle_sidebar_var: false,
      clicked: false,
      toggle_rightsidebar_var: false,
      rightclicked: false,
      mobile_toggle: false,
      mobile_search: false,
      mobiletoggle1: false,
      mobiletoggle2: false,
      user: {},
      plan_details: {},
      downMonitorCounts: 5,
      user_role: '',
      showProfileDropdown: false,
      planFound: null,
      planExpired: false,
    }
  },
  mounted() {
    this.user = JSON.parse(localStorage.getItem('userinfo'))
    this.user_role = this.user.role
    this.getPlanInfo();
    if (this.user_role == 'user' || this.user_role == 'subUser') {
      //run get data in every 1 minutes
      this.getData();
      setInterval(() => {
        this.getData();
      }, 60000);
    }
  },
  computed: {
    
    ...mapState({
      menuItems: state => state.menu.searchData,
      layout: state => state.layout.layout,
      togglesidebar: (state) => state.menu.togglesidebar
    }),
    isMobileScreen() {
      return window.innerWidth <= 1200;
    }
  },
  // mounted() {
  //   // this.checkPlan()
  // },
  methods: {
    async checkPlan() {
      let response = await UserController.getPlanInfo();
      if (response.result) {
        let plan = response.message 
        let plan_type = plan.plan_type
        let expiry_date = new Date(plan.expiry_date)
        let today = new Date()
        if (plan_type == 1 || today >= expiry_date || plan == "PLAN_NOT_FOUND") {
          this.$router.replace("/auth/verification");
        }
      }
    },
    async getData() {
      this.user = JSON.parse(localStorage.getItem('userinfo'))
      this.user_role = this.user.role
      if (this.user_role == 'user' || this.user_role == 'subUser') {
        this.checkPlan();
      }
      let response = await DashboardController.getMonitorReport();
      if (response.message) {
        this.downMonitorCounts = response.data.down;
      }
      const faviconElement = document.getElementById("dynamic-favicon");
      let faviconUrl;
      const notificationCount = this.downMonitorCounts;
      if (notificationCount > 20) {
        faviconUrl = '/icon/20_plus.png';
      }
      else if (notificationCount > 0) {
        faviconUrl = `/icon/${notificationCount}.png`;
      }
      else {
        faviconUrl = '/favicon.png';
      }
      faviconElement.href = faviconUrl;
    },
    changeDateFormate(d) {
      return moment(d).format('DD/MM/YYYY')
    },
    formatBillingDate(d) {
      return moment(d).format('DD MMM YYYY')
    },
    endAfterStart(start, end) {
      var startDate = new Date(start);
      var endDate = new Date(end);

      return endDate.getTime() >= startDate.getTime();
    },
    async getPlanInfo() {
      let response = await UserController.getPlanInfo();
      if (response.result) {
        this.plan_details = response.message;
        console.log("Plan Details: ", this.plan_details);
        this.$root.$data.plan_details = this.plan_details;
        
        // Update plan status
        if(response.message == 'PLAN_NOT_FOUND'){
          this.planFound = false;
        } else if(response.message.expiry_date && new Date(response.message.expiry_date) < new Date()) {
          this.planExpired = true;
        } else {
          this.planFound = true;
          this.planExpired = false;
        }
      }
    },
    updatePlanStatus(status) {
      this.planFound = status.planFound;
      this.planExpired = status.planExpired;
    },
    toggle_sidebar() {
      this.$store.dispatch("menu/opensidebar");
    },
    toggleProfileDropdown() {
      this.showProfileDropdown = !this.showProfileDropdown;
    },
    closeProfileDropdown() {
      this.showProfileDropdown = false;
    },
    openmobilemenu1() {
      this.mobiletoggle1 = !this.mobiletoggle1
    },
    openmobilemenu2() {
      this.mobiletoggle2 = !this.mobiletoggle2
    },
    search_open() {
      this.searchOpen = true
    },
    search_close() {
      this.searchOpen = false
    },
    searchTerm: function () {
      this.$store.dispatch('menu/searchTerm', this.terms)
    },
    logout: function () {
      // firebase.auth().signOut().then(() => {
      UserAuth.Logout();
      this.$router.replace('/auth/login')
      // })
    },
    addFix() {
      body.classList.add("offcanvas")
      this.searchResult = true;
    },
    removeFix() {
      router
      body.classList.remove("offcanvas")
      this.searchResult = false;
      this.terms = ''
    },
    // toggle_sidebar(){
    //   this.toggle_sidebar_var = !this.toggle_sidebar_var,
    //   this.clicked = !this.toggle_sidebar_var,
    //   this.$emit('clicked',this.clicked)
    // },
    toggle_rightsidebar() {
      this.toggle_rightsidebar_var = !this.toggle_rightsidebar_var,
        this.rightclicked = !this.toggle_rightsidebar_var,
        this.$emit('rightclicked', this.rightclicked)
    },
    toggle_fullscreen() {
      if ((document.fullScreenElement && document.fullScreenElement !== null) ||
        (!document.mozFullScreen && !document.webkitIsFullScreen)) {
        if (document.documentElement.requestFullScreen) {
          document.documentElement.requestFullScreen();
        } else if (document.documentElement.mozRequestFullScreen) {
          document.documentElement.mozRequestFullScreen();
        } else if (document.documentElement.webkitRequestFullScreen) {
          document.documentElement.webkitRequestFullScreen(Element.ALLOW_KEYBOARD_INPUT);
        }
      } else {
        if (document.cancelFullScreen) {
          document.cancelFullScreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitCancelFullScreen) {
          document.webkitCancelFullScreen();
        }
      }
    },
    async visitedUser() {
      let response = await UserController.visitedUser()
    }
  },
  watch: {
    menuItems: function () {
      this.terms ? this.addFix() : this.removeFix();
      if (!this.menuItems.length)
        this.searchResultEmpty = true;
      else
        this.searchResultEmpty = false;
    },
    upgrade_modal() {
      if (this.upgrade_modal == true) {
        this.visitedUser()
      }
    }
  }
}
</script>

<style scoped>
/* Header Container */
.page-main-header {
  background-color: #FFFFFF;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  height: 80px;
}

.page-main-header.open {
  margin-left: 0;
}

.main-header-right {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 0;
}

/* Left Section */
.header-left-section {
  display: flex;
  align-items: center;
  gap: 20px;
}

.menu-toggle-icon {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  transition: all 0.3s ease;
}

.menu-toggle-icon:hover {
  opacity: 0.7;
}

/* Sidebar toggle icons */
.menu-toggle-icon img {
  width: 32px;
  height: 32px;
}

.page-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 22px;
  line-height: 1.5;
  color: #111111;
  margin: 0;
}

/* Right Section */
.header-right-section {
  display: flex;
  align-items: center;
  gap: 20px;
}

/* Billing and Upgrade Container */
.billing-upgrade-container {
  display: flex;
  align-items: stretch;
  height: 48px;
  border-radius: 25px;
  background-color: #E8EEFD;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Billing Date Section */
.billing-date-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #E8EEFD;
  padding: 0 20px;
  min-width: 160px;
}

.billing-label {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1;
  color: #000000;
  margin: 0;
}

.billing-date {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.2;
  color: #000000;
  margin: 2px 0 0 0;
}

/* Upgrade Section */
.upgrade-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #2FB234;
  color: #FFFFFF;
  border: none;
  padding: 0 24px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 18px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  white-space: nowrap;
  border-top-left-radius: 25px;
  border-bottom-left-radius: 25px;
}

.upgrade-section:hover {
  background-color: #27A02C;
}

.upgrade-section:focus {
  outline: none;
  box-shadow: none;
}

.upgrade-section svg {
  width: 20px;
  height: 20px;
}

.upgrade-section span {
  margin: 0;
}

/* Profile Container */
.profile-container {
  position: relative;
}

.profile-dropdown-wrapper {
  position: relative;
}

.profile-button {
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: #E8EEFD;
  border-radius: 12px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.profile-button:hover {
  background-color: #d4defc;
}

.profile-button svg {
  width: 24px;
  height: 24px;
}

.profile-button span {
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 1.5;
  letter-spacing: 0.02em;
  color: #000000;
  margin: 0 5px;
}

.dropdown-arrow {
  margin-left: 5px;
  transition: transform 0.3s ease;
}

.dropdown-arrow.rotate {
  transform: rotate(180deg);
}

/* Profile Dropdown Menu */
.profile-dropdown-menu {
  position: absolute;
  top: calc(100% + -48px);
  right: 0;
  width: 500px;
  background: #FFFFFF;
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 1px solid #D2E3FB;
  z-index: 1000;
  overflow: hidden;
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Dropdown Header */
.dropdown-header {
  display: flex;
  align-items: center;
  padding: 0;
  border-bottom: none;
  position: relative;
  gap: 5px;
}

.profile-icon {
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-icon svg {
  width: 26px;
  height: 26px;
}

.profile-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 24px;
  color: #3A3A3A;
  flex: 1;
  line-height: 0.915em;
}

.view-profile-btn {
  background-color: #E8EEFD;
  color: #000000;
  padding: 8px 10px;
  border-radius: 12px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 18px;
  text-decoration: none;
  transition: all 0.3s ease;
  line-height: 1.5;
  letter-spacing: 1.11%;
}

.view-profile-btn:hover {
  background-color: #d4defc;
  color: #000000;
  text-decoration: none;
}

.close-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  transition: all 0.3s ease;
  margin-left: 20px;
}

.close-btn:hover {
  opacity: 0.7;
}

.close-btn img {
  width: 30px;
  height: 30px;
}

/* Profile Info Card */
.profile-info-card {
  padding: 20px;
  background-color: #FFFFFF;
  margin: 0;
  border-radius: 20px;
  border: 1px solid #D2E3FB;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-row {
  margin-bottom: 0;
}

.info-row:last-child {
  margin-bottom: 0;
}

.email {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #3A3A3A;
  word-break: break-all;
  line-height: 0.915em;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 30px;
}

.info-item .label {
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: #3A3A3A;
  line-height: 0.915em;
}

.info-item .value {
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: #3A3A3A;
  text-align: right;
  line-height: 0.915em;
}

.info-item .value.plan-name {
  font-weight: 600;
  color: #000000;
  line-height: 1.5;
  letter-spacing: 1.11%;
}

/* Logout Button */
.logout-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #FDE6E6;
  color: #F20707;
  border: none;
  border-radius: 12px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  line-height: 1.5;
}

.logout-btn:hover {
  background-color: #fdd4d4;
}

/* Dropdown Animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.3s ease;
}

.dropdown-fade-enter,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Modal Logout Button */
.logout-modal-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #FFF5F5;
  color: #E53E3E;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: absolute;
  left: 0;
}

.logout-modal-btn:hover {
  background: #FFE0E0;
}

/* Remove border when plan exists */
.custom-modal-header.no-border {
  border-bottom: none !important;
  padding-bottom: 0 !important;
}

/* Override bootstrap modal title for upgrade modal */
#modal-upgrade-plan .modal-title {
  display: none !important;
}

#modal-upgrade-plan .modal-header {
  padding: 0 !important;
}

#modal-upgrade-plan .modal-header .w-100 {
  padding: 30px 30px 0 30px;
}

/* Ensure modal content doesn't overflow */
#modal-upgrade-plan .modal-content {
  overflow: visible;
}

#modal-upgrade-plan .modal-body {
  overflow: visible;
}

/* Custom upgrade modal header */
.custom-upgrade-modal-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;
  min-height: 60px;
  padding: 30px;
  width: calc(100% + 60px);
  border-bottom: 1px solid #D2E3FB;
}

.custom-upgrade-modal-header.no-border {
  border-bottom: none !important;
}

.custom-upgrade-modal-header .logout-modal-btn {
  position: absolute;
  left: 30px;
  top: 50%;
  transform: translateY(-50%);
}

.custom-modal-close {
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 36px;
  line-height: 1;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.custom-modal-close:hover {
  color: #333;
  transform: translateY(-50%) scale(1.1);
}

.custom-modal-close:focus {
  outline: none;
}

.custom-upgrade-modal-header .custom-modal-close {
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
}

/* Remove all header styling for this modal */
.custom-modal-no-header .modal-header {
  display: none !important;
}

.custom-modal-no-header .modal-body {
  padding-top: 0 !important;
}

/* Medium Screen Styles (for screens like 1366x768) */
@media screen and (min-width: 1201px) and (max-width: 1440px) {
  .profile-dropdown-menu {
    width: 420px;
    padding: 25px;
    gap: 20px;
    top: calc(100% + -48px);
  }

  .profile-title {
    font-size: 22px;
  }

  .view-profile-btn {
    font-size: 16px;
    padding: 6px 12px;
  }

  .profile-info-card {
    padding: 18px;
    gap: 18px;
  }

  .email {
    font-size: 16px;
  }

  .info-item .label {
    font-size: 16px;
  }

  .info-item .value {
    font-size: 16px;
  }

  .logout-btn {
    font-size: 16px;
    padding: 10px 18px;
  }

  .close-btn {
    width: 26px;
    height: 26px;
  }

  .close-btn svg {
    width: 26px;
    height: 26px;
  }
}

/* Mobile Styles */
@media screen and (max-width: 1200px) {
  .page-main-header {
    padding: 10px 15px;
    height: 60px;
  }

  .page-title {
    font-size: 18px;
  }

  .billing-upgrade-container {
    height: 40px;
    border-radius: 20px;
  }

  .billing-date-section {
    display: none;
  }

  .upgrade-section {
    font-size: 14px;
    padding: 0 16px;
    border-radius: 20px;
  }

  .upgrade-section svg {
    width: 16px;
    height: 16px;
  }

  .profile-button span {
    display: none;
  }

  .profile-button {
    padding: 8px;
  }

  .dropdown-arrow {
    display: none;
  }

  .profile-dropdown-menu {
    width: 320px;
    padding: 20px;
    gap: 15px;
    top: calc(100% + 5px);
  }

  .dropdown-header {
    padding: 0;
  }

  .profile-title {
    font-size: 20px;
  }

  .view-profile-btn {
    font-size: 14px;
    padding: 5px 10px;
  }

  .profile-info-card {
    padding: 16px;
    margin: 0;
    gap: 16px;
  }

  .email {
    font-size: 16px;
  }

  .info-item .label {
    font-size: 14px;
  }

  .info-item .value {
    font-size: 16px;
  }

  .logout-btn {
    font-size: 16px;
    padding: 8px 16px;
  }

  .logout-btn svg {
    width: 20px;
    height: 20px;
  }
}

@media screen and (max-width: 768px) {
  .header-left-section {
    gap: 10px;
  }

  .menu-toggle-icon {
    width: 36px;
    height: 36px;
  }

  /* Sidebar toggle icons */
  .menu-toggle-icon img {
    width: 26px;
    height: 26px;
  }

  .page-title {
    font-size: 16px;
  }

  .upgrade-button {
    font-size: 12px;
    padding: 6px 12px;
    height: 36px;
  }

  .upgrade-button svg {
    width: 18px;
    height: 18px;
  }

  /* Profile Dropdown Mobile Styles */
  .profile-dropdown-menu {
    width: 280px;
    top: calc(100% + 5px);
    padding: 16px;
    gap: 12px;
  }

  .dropdown-header {
    padding: 0;
    gap: 3px;
  }

  .profile-icon {
    width: 22px;
    height: 22px;
  }

  .profile-icon svg {
    width: 22px;
    height: 22px;
  }

  .profile-title {
    font-size: 18px;
  }

  .view-profile-btn {
    font-size: 12px;
    padding: 4px 8px;
  }

  .close-btn {
    width: 24px;
    height: 24px;
    margin-left: 10px;
  }

  .close-btn svg {
    width: 24px;
    height: 24px;
  }

  .profile-info-card {
    padding: 14px;
    margin: 0;
    gap: 14px;
  }

  .info-row {
    margin-bottom: 0;
  }

  .email {
    font-size: 14px;
  }

  .info-item .label {
    font-size: 12px;
  }

  .info-item .value {
    font-size: 14px;
  }

  .logout-btn {
    font-size: 14px;
    padding: 8px 14px;
  }

  .logout-btn svg {
    width: 18px;
    height: 18px;
  }
}
</style>

<style>
/* Override existing header styles to match Figma design */
.page-wrapper .page-main-header {
  height: 80px !important;
  align-items: center !important;
  display: flex !important;
}

.page-wrapper .page-body-wrapper .page-body {
  margin-top: 80px !important;
}

/* Remove old header style conflicts */
.page-main-header .main-header-right .mobile-sidebar,
.page-main-header .main-header-right .nav-right,
.page-main-header .main-header-right .nav-menus-left {
  display: none !important;
}

/* Ensure our new layout is visible */
.page-main-header .header-left-section,
.page-main-header .header-right-section {
  display: flex !important;
}

@media screen and (max-width: 1200px) {
  .page-wrapper .page-main-header {
    height: 60px !important;
  }
  
  .page-wrapper .page-body-wrapper .page-body {
    margin-top: 60px !important;
  }
}

@media (max-width: 768px) {
  .page-wrapper .page-main-header {
    height: 60px !important;
  }

  .page-wrapper .page-body-wrapper .page-body {
    margin-top: 60px !important;
  }
}
</style>
